import { useMutation } from "@tanstack/react-query";
import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { apiFetch } from "@/lib/api-client";

export type DesignNodeLabel = "UserJourney" | "Flow" | "Page" | "Component";

export interface CreateDesignNodePayload {
  label: DesignNodeLabel;
  data: Record<string, any>;
}

export async function createDesignNode(
  payload: CreateDesignNodePayload,
  uuid: string,
  token: string,
): Promise<{ data: any; label: DesignNodeLabel }> {
  const url = `${config.isometricApiUrl}/design-ontology-graph/${uuid}/create-node`;
  try {
    const response = await apiFetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.message || "Request failed");
      error.status = response.status;
      throw error;
    }
    const data = await response.json();
    return { ...data, label: payload.label };
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
}

export const useCreateDesignNode = (
  onSettled?: (
    res: { data: any; label: DesignNodeLabel } | undefined,
    error: Error | null,
  ) => void,
) => {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (payload: { body: CreateDesignNodePayload; uuid: string }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return createDesignNode(payload.body, payload.uuid, token);
    },
    onSettled,
  });
};
